import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PdfFileRow } from "../../db/pdfRepository";
import type { SortOption } from "../../components/primitives/SortDropdown";

export type PdfSortKey =
  | "date_desc"
  | "date_asc"
  | "name_asc"
  | "name_desc"
  | "size_desc"
  | "size_asc";

const sorters: Record<PdfSortKey, (a: PdfFileRow, b: PdfFileRow) => number> = {
  date_desc: (a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  date_asc: (a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  name_asc: (a, b) => (a.file_name || "").localeCompare(b.file_name || "", "ko"),
  name_desc: (a, b) => (b.file_name || "").localeCompare(a.file_name || "", "ko"),
  size_desc: (a, b) => (b.file_size || 0) - (a.file_size || 0),
  size_asc: (a, b) => (a.file_size || 0) - (b.file_size || 0),
};

export function usePdfFilterSort(pdfList: PdfFileRow[], folderId: string | null) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState<PdfSortKey>("date_desc");

  const filteredAndSorted = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return pdfList
      .filter((pdf) => {
        if (folderId !== null && pdf.folder_id !== folderId) return false;
        if (!query) return true;
        return pdf.file_name?.toLowerCase().includes(query);
      })
      .sort(sorters[sortOption]);
  }, [pdfList, folderId, searchQuery, sortOption]);

  const sortOptions: SortOption<PdfSortKey>[] = useMemo(
    () => [
      { key: "date_desc", label: t("dashboard.sort_date_desc") },
      { key: "date_asc", label: t("dashboard.sort_date_asc") },
      { key: "name_asc", label: t("dashboard.sort_name_asc") },
      { key: "name_desc", label: t("dashboard.sort_name_desc") },
      { key: "size_desc", label: t("dashboard.sort_size_desc") },
      { key: "size_asc", label: t("dashboard.sort_size_asc") },
    ],
    [t]
  );

  return {
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
    sortOptions,
    filteredAndSorted,
  };
}
